/** Tool definitions: Quotes and market-data provider diagnostics. */

import type { ToolDefinition } from './types.js';

export const marketDebugTools = {
  get_quote: {
    name: 'get_quote',
    access: 'read',
    description:
      'Get real-time stock quote for a symbol including current price, change, percent change, bid/ask, volume, and previous close. Useful for checking current market prices when discussing trades or portfolio values.',
    inputSchema: {
      type: 'object',
      properties: {
        symbol: {
          type: 'string',
          description: 'Stock symbol to get quote for (e.g., "AAPL", "TSLA", "SPY").',
        },
      },
      required: ['symbol'],
    },
  },
  get_quotes_batch: {
    name: 'get_quotes_batch',
    access: 'read',
    description:
      'Get live quotes for one or more symbols directly from the quote service. Returns price, change, changePercent, previousClose, bid/ask, and which provider served the data. Useful for debugging quote provider fallbacks.',
    inputSchema: {
      type: 'object',
      properties: {
        symbols: {
          type: 'array',
          items: { type: 'string' },
          description: 'Symbols to fetch (e.g. ["AAPL", "QQQ260417C00605000"]). Supports stocks and OCC option symbols.',
        },
        provider: {
          type: 'string',
          enum: ['alpaca', 'yahoo', 'finnhub', 'alphavantage'],
          description: 'Force a specific provider. Omit to use normal priority/fallback logic.',
        },
      },
      required: ['symbols'],
    },
  },
  get_provider_status: {
    name: 'get_provider_status',
    access: 'read',
    description:
      'Get the current status of all quote providers (Alpaca, Yahoo Finance, Alpha Vantage) including whether they are enabled, rate limited, their priority, request counts, and quota usage. Use this to diagnose provider fallback issues.',
    inputSchema: {
      type: 'object',
      properties: {},
      required: [],
    },
  },
  get_quote_cache_stats: {
    name: 'get_quote_cache_stats',
    access: 'read',
    description:
      'Get quote cache statistics including cache hit/miss rates, TTL settings, and number of cached symbols. Useful for understanding if stale cache data is affecting quotes.',
    inputSchema: {
      type: 'object',
      properties: {},
      required: [],
    },
  },
  clear_quote_cache: {
    name: 'clear_quote_cache',
    access: 'write',
    description:
      'Clear the in-memory quote cache, forcing fresh fetches from providers on the next request. Use this to get fresh data after a provider outage or to debug caching issues.',
    inputSchema: {
      type: 'object',
      properties: {},
      required: [],
    },
  },
  get_option_quote_debug: {
    name: 'get_option_quote_debug',
    access: 'read',
    description:
      'Get a live quote for a single OCC option symbol directly from the quote service, bypassing portfolio context. Returns price, change, changePercent, previousClose, bid/ask, greeks, and provider. Use this to debug why option daily change shows $0.00.',
    inputSchema: {
      type: 'object',
      properties: {
        symbol: {
          type: 'string',
          description: 'OCC option symbol (e.g. "QQQ260417C00605000").',
        },
        provider: {
          type: 'string',
          enum: ['alpaca', 'yahoo', 'finnhub', 'alphavantage'],
          description: 'Force a specific provider. Omit to use normal priority/fallback logic.',
        },
      },
      required: ['symbol'],
    },
  },
  diagnose_symbol: {
    name: 'diagnose_symbol',
    access: 'read',
    description:
      'For one symbol (stock or OCC option), sequentially probe every registered provider and return per-provider {ok, ms, errorMessage, price/bid/ask, rateLimit, quota}. By default, ignores availability gates so you can see exactly what each provider returns *right now* — even ones the orchestrator would normally skip due to rate-limit state. Best first step when a specific symbol fails.',
    inputSchema: {
      type: 'object',
      properties: {
        symbol: {
          type: 'string',
          description: 'Stock symbol (e.g. "AAPL") or OCC option symbol (e.g. "QQQ260417C00605000").',
        },
        ignoreAvailability: {
          type: 'boolean',
          description: 'If true (default), probe every provider regardless of rate-limit/quota state. Set to false to mirror normal orchestrator behavior.',
        },
      },
      required: ['symbol'],
    },
  },
  probe_providers_health: {
    name: 'probe_providers_health',
    access: 'read',
    description:
      'Hit every provider in parallel with a sentinel symbol (default AAPL) and return latency, success, and current rate-limit/quota state. One-shot "are all providers actually alive right now?" health check.',
    inputSchema: {
      type: 'object',
      properties: {
        sentinel: {
          type: 'string',
          description: 'Sentinel symbol to fetch from each provider. Defaults to "AAPL".',
        },
      },
      required: [],
    },
  },
  reset_provider_state: {
    name: 'reset_provider_state',
    access: 'write',
    description:
      "Clear a provider's rate-limit window, quota counter, and (for Yahoo) the in-process 429 cooldown flag. Use this to un-stick a provider that's stuck skipped without restarting the API. Returns before/after state.",
    inputSchema: {
      type: 'object',
      properties: {
        provider: {
          type: 'string',
          enum: ['alpaca', 'yahoo', 'finnhub', 'alphavantage'],
          description: 'Provider name to reset.',
        },
      },
      required: ['provider'],
    },
  },
  get_recent_quote_failures: {
    name: 'get_recent_quote_failures',
    access: 'read',
    description:
      'Return the orchestrator\'s rolling ring buffer of recent per-symbol fetch failures. Each entry has {symbol, provider, reason, timestamp, kind}. Useful for spotting a pattern (e.g. all failures hit alphavantage with "rate limit" — Yahoo is silently being skipped).',
    inputSchema: {
      type: 'object',
      properties: {
        limit: {
          type: 'number',
          description: 'Max number of recent failures to return (most recent first). Defaults to 50, max 200.',
        },
      },
      required: [],
    },
  },
  // A "get" must not mutate (API-11): clearing is its own write tool, backed
  // by the admin-only DELETE /market/quote-failures.
  clear_recent_quote_failures: {
    name: 'clear_recent_quote_failures',
    access: 'write',
    description: 'Clear the orchestrator\'s ring buffer of recent quote-fetch failures (admin only).',
    inputSchema: { type: 'object', properties: {}, required: [] },
  },
} satisfies Record<string, ToolDefinition>;
